'use client'

import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { cx } from '@/app/lib/utils'
import { FieldMessage, useFieldIds } from '@/app/components/atoms/forms/field'
import {
  IconCheck,
  IconChevDown,
  IconChevUp,
  IconClose,
  IconSearch,
} from '@/app/components/atoms/Icons'
import type { SelectFieldProps } from './SelectField.types'
import styles from './SelectField.module.scss'

/** Insensible a mayúsculas y acentos (p. ej. "Coruña" ~ "coruna"), para buscar en las opciones. */
function normalizeForSearch(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

/**
 * Nombre accesible de una opción con recuento: el número entre paréntesis se
 * lee mal ("Pop paréntesis 24"), así que se explicita el sustantivo. Empieza
 * por la etiqueta visible para no romper "Label in Name" (WCAG 2.5.3).
 */
function optionA11yLabel(label: string, count: number): string {
  return `${label}, ${count} ${count === 1 ? 'resultado' : 'resultados'}`
}

/**
 * Siguiente índice habilitado a partir de `fromIndex` (excluido), circular, en
 * la dirección indicada. Si todas las opciones están deshabilitadas, devuelve
 * `fromIndex` sin moverse.
 */
function nextEnabledIndex(
  options: SelectFieldProps['options'],
  fromIndex: number,
  direction: 1 | -1,
): number {
  const count = options.length
  for (let step = 1; step <= count; step += 1) {
    const index = (fromIndex + direction * step + count) % count
    if (!options[index]?.disabled) return index
  }
  return fromIndex
}

/**
 * Campo de selección única del DS: disparador de 56px con label flotante +
 * desplegable con buscador y lista de opciones (marca de check en la
 * seleccionada). Patrón combobox (mismo que `atoms/CityPicker`): las flechas
 * dentro del buscador mueven la opción "activa" y Enter la selecciona; cada
 * opción también es clicable con el puntero. Cierra y devuelve el foco al
 * disparador al elegir — a diferencia de `MultiSelectField`, no necesita
 * botones de confirmar/borrar porque cada selección ya es la definitiva.
 *
 * Client Component: gestiona apertura, búsqueda y cierre por click fuera /
 * pérdida de foco / Escape.
 */
export function SelectField({
  label,
  value = '',
  options,
  placeholder,
  clearable = true,
  emptyValue = '',
  errorText,
  disabled = false,
  required = false,
  variant = 'default',
  onChange,
  id,
}: SelectFieldProps) {
  const { fieldId, messageId } = useFieldIds(id)
  const generatedListId = useId()
  const listId = `${fieldId}-listbox`
  const optionId = (index: number) => `${generatedListId}-option-${index}`

  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)

  const hasError = Boolean(errorText)
  const isFilled = value !== emptyValue
  const selectedOption = options.find((option) => option.value === value)
  const displayText = selectedOption?.label ?? (isFilled ? undefined : placeholder)

  const filteredOptions = useMemo(() => {
    if (query === '') return options
    const normalizedQuery = normalizeForSearch(query)
    return options.filter((option) => normalizeForSearch(option.label).includes(normalizedQuery))
  }, [options, query])

  const maxIndex = Math.max(0, filteredOptions.length - 1)
  const safeActiveIndex = clamp(activeIndex, 0, maxIndex)

  // Foco del buscador al abrir: efecto puramente de DOM (el input todavía no
  // existe en el render donde se pulsa el disparador), sin tocar estado.
  useEffect(() => {
    if (isOpen) searchInputRef.current?.focus()
  }, [isOpen])

  // Cierra al hacer click fuera o al mover el foco fuera del contenedor.
  useEffect(() => {
    if (!isOpen) return
    const container = containerRef.current

    const handleOutside = (event: MouseEvent) => {
      if (!container?.contains(event.target as Node)) close(false)
    }
    const handleFocusOut = (event: FocusEvent) => {
      // `relatedTarget` es `null` cuando el foco no aterriza en ningún
      // elemento concreto — no solo al perder el foco genuinamente hacia
      // fuera, también cuando el propio elemento enfocado se deshabilita en
      // el mismo gesto (el navegador fuerza un blur sin `relatedTarget`). No
      // es fiable como "el foco salió del desplegable" — ver
      // `RangeDropdownField.tsx` (mismo bug, ya corregido ahí). El cierre por
      // click fuera ya lo cubre `handleOutside` (mousedown); este handler
      // solo hace falta para el Tab del teclado, que sí entrega un
      // `relatedTarget` real.
      const relatedTarget = event.relatedTarget as Node | null
      if (relatedTarget && !container?.contains(relatedTarget)) close(false)
    }

    document.addEventListener('mousedown', handleOutside)
    container?.addEventListener('focusout', handleFocusOut)
    return () => {
      document.removeEventListener('mousedown', handleOutside)
      container?.removeEventListener('focusout', handleFocusOut)
    }
  }, [isOpen])

  // Abre el desplegable y fija la búsqueda/opción activa en el mismo gesto
  // que lo abre (clic o flecha) — en vez de un efecto reaccionando a `isOpen`,
  // que dispararía un re-render adicional para ajustar estado derivado.
  function open() {
    setQuery('')
    const selectedIndex = options.findIndex((option) => option.value === value)
    setActiveIndex(Math.max(0, selectedIndex))
    setIsOpen(true)
  }

  function close(returnFocus: boolean) {
    setIsOpen(false)
    setQuery('')
    if (returnFocus) triggerRef.current?.focus()
  }

  function handleSelect(optionValue: string) {
    onChange?.(optionValue)
    close(true)
  }

  function handleClear() {
    onChange?.(emptyValue)
  }

  function handleTriggerKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
    if (disabled) return
    if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && !isOpen) {
      event.preventDefault()
      open()
    }
  }

  function handleSearchKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    switch (event.key) {
      case 'Escape':
        event.preventDefault()
        close(true)
        break
      case 'ArrowDown':
        event.preventDefault()
        if (filteredOptions.length > 0)
          setActiveIndex((previous) => nextEnabledIndex(filteredOptions, previous, 1))
        break
      case 'ArrowUp':
        event.preventDefault()
        if (filteredOptions.length > 0)
          setActiveIndex((previous) => nextEnabledIndex(filteredOptions, previous, -1))
        break
      case 'Enter':
        event.preventDefault()
        if (filteredOptions.length > 0 && !filteredOptions[safeActiveIndex]?.disabled) {
          handleSelect(filteredOptions[safeActiveIndex]!.value)
        }
        break
      default:
        break
    }
  }

  return (
    <div className={styles.root}>
      <div
        ref={containerRef}
        className={cx(
          styles.field,
          isOpen && styles.active,
          disabled && styles.disabled,
          hasError && styles.error,
        )}
        data-variant={variant === 'inverse' ? 'inverse' : undefined}
      >
        <button
          ref={triggerRef}
          type="button"
          id={fieldId}
          className={styles.trigger}
          onClick={() => {
            if (disabled) return
            if (isOpen) close(false)
            else open()
          }}
          onKeyDown={handleTriggerKeyDown}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={isOpen ? listId : undefined}
          aria-label={selectedOption ? `${label}: ${selectedOption.label}` : label}
          aria-describedby={hasError ? messageId : undefined}
          disabled={disabled}
        >
          <span
            className={cx(
              styles.label,
              // `displayText`, no solo `isFilled`: un `value === emptyValue` que
              // coincide con una opción (p. ej. el orden por defecto de `/search`)
              // se pinta igualmente y el label no debe quedar encima.
              (isFilled || isOpen || Boolean(placeholder) || Boolean(displayText)) && styles.labelElevated,
            )}
          >
            {label}
            {required && <span aria-hidden="true"> *</span>}
          </span>
          {displayText && <span className={styles.value}>{displayText}</span>}
        </button>

        {/* Fuera del `<button>` del disparador: el de borrar es un botón real
            (no se puede anidar `<button>` dentro de `<button>`), superpuesto
            en el mismo hueco absolute que el chevron. Solo cuando el valor no
            es ya `emptyValue`, el desplegable está cerrado y el campo admite
            borrarse (`clearable`) — abierto, el usuario ya está eligiendo. Al
            pulsarlo, `onChange(emptyValue)` — no necesariamente `''` (p. ej.
            una provincia acoplada a la ruta vuelve a `"todos"`, no a vacío). */}
        {isFilled && !isOpen && clearable ? (
          <button
            type="button"
            className={styles.clearButton}
            aria-label={`Borrar ${label}`}
            onClick={handleClear}
            disabled={disabled}
          >
            <IconClose size={16} />
          </button>
        ) : (
          <span className={styles.chevron} aria-hidden="true">
            {isOpen ? <IconChevUp size={24} /> : <IconChevDown size={24} />}
          </span>
        )}

        {isOpen && (
          <div className={styles.dropdown}>
            <div className={styles.searchRow}>
              <IconSearch size={20} aria-hidden="true" />
              <input
                ref={searchInputRef}
                type="text"
                role="combobox"
                className={styles.searchInput}
                placeholder="Buscar…"
                autoComplete="off"
                aria-autocomplete="list"
                aria-expanded={isOpen}
                aria-controls={listId}
                aria-activedescendant={
                  filteredOptions.length > 0 ? optionId(safeActiveIndex) : undefined
                }
                aria-label={`Buscar en ${label}`}
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value)
                  setActiveIndex(0)
                }}
                onKeyDown={handleSearchKeyDown}
              />
            </div>

            <ul id={listId} role="listbox" aria-label={label} className={styles.optionList}>
              {filteredOptions.length === 0 ? (
                <li role="presentation" className={styles.noResults}>
                  Sin resultados
                </li>
              ) : (
                filteredOptions.map((option, index) => {
                  const selected = option.value === value
                  const active = index === safeActiveIndex
                  return (
                    <li
                      key={option.value}
                      id={optionId(index)}
                      role="option"
                      aria-selected={selected}
                      aria-disabled={option.disabled || undefined}
                      aria-label={
                        option.count === undefined
                          ? undefined
                          : optionA11yLabel(option.label, option.count)
                      }
                      className={cx(
                        styles.option,
                        selected && styles.optionSelected,
                        active && styles.optionActive,
                        option.disabled && styles.optionDisabled,
                      )}
                      onMouseEnter={() => {
                        if (!option.disabled) setActiveIndex(index)
                      }}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        if (!option.disabled) handleSelect(option.value)
                      }}
                    >
                      <span>
                        {option.label}
                        {option.count !== undefined && (
                          <span className={styles.optionCount}> ({option.count})</span>
                        )}
                      </span>
                      {selected && (
                        <span className={styles.checkIcon} aria-hidden="true">
                          <IconCheck size={16} />
                        </span>
                      )}
                    </li>
                  )
                })
              )}
            </ul>
          </div>
        )}
      </div>

      <FieldMessage id={messageId} error={errorText} />
    </div>
  )
}
